import ProductBuildingType from '@deltek/specification-client/core/Constants/productBuildingType'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import { NameValuePair } from '@deltek/specification-client/core/Models/nameValuePair'
import Checkbox from '@material-ui/core/Checkbox'
import Chip from '@material-ui/core/Chip'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import Autocomplete, {
    AutocompleteChangeReason,
} from '@material-ui/lab/Autocomplete'
import clsx from 'clsx'
import React from 'react'
import { ProductListingDetailFormField } from '../ListingDetailsPage'

const useStyles = makeStyles((theme: Theme) => ({
    container: { paddingTop: theme.spacing(2) },
    autocompleteRoot: {
        width: 244,
        borderRadius: 4,
    },
    autocompletePopupIndicator: {
        padding: 0,
    },
    chipLabel: {
        ...theme.typography.h4,
        fontWeight: 'normal',
        overflow: 'unset',
        textOverflow: 'unset',
        whiteSpace: 'normal',
        maxWidth: 160,
    },
    checkbox: { marginRight: theme.spacing(1) },
    autocompleteInputAdornment: {
        maxHeight: 72,
        overflowY: 'auto',
    },
    chipLabelWidth: {
        maxWidth: 140,
    },
}))

const getAutocompleteValues = (values: string[]) => {
    const localValues = values.map(
        (eachValue) =>
            ProductBuildingType.filter(
                (eachOption) => eachOption.value === eachValue
            )?.[0] || {
                name: eachValue,
                value: eachValue,
            }
    )
    if (values.length === ProductBuildingType.length) {
        localValues.push({ name: 'All', value: 'all' })
    }

    return localValues
}

interface EditBuildingTypeProps {
    pastValue: string[]
    valid?: boolean
    handleFieldChange: (
        field: ProductListingDetailFormField,
        value: string[]
    ) => void
}

export const EditBuildingType: React.FC<EditBuildingTypeProps> = ({
    pastValue,
    valid,
    handleFieldChange,
}) => {
    const classes = useStyles()
    const [localValue, setLocalValue] = React.useState<string[]>(pastValue)

    const options = [
        { name: 'All', value: 'all' },
        ...ProductBuildingType,
    ] as NameValuePair<string>[]

    const optionLabel = (option: NameValuePair<string>) => option.name

    const handleChange = (
        _event: React.ChangeEvent<{}>,
        value: NameValuePair<string>[],
        reason: AutocompleteChangeReason
    ) => {
        if (reason === 'select-option' || reason === 'remove-option') {
            if (
                // if current value does not have 'All' and it is selected before
                // clear all
                pastValue.length === ProductBuildingType.length &&
                !value.some((each) => each.value === 'all')
            ) {
                setLocalValue([])
                handleFieldChange('buildingTypes', [])
            } else if (value.some((each) => each.value === 'all')) {
                if (pastValue.length !== ProductBuildingType.length) {
                    // if current value has 'All' and it is not selected before
                    // check all
                    setLocalValue(ProductBuildingType.map((each) => each.value))
                    handleFieldChange(
                        'buildingTypes',
                        ProductBuildingType.map((each) => each.value)
                    )
                } else {
                    // if current value has 'All' and it is selected before
                    // uncheck all and selected value other than all
                    setLocalValue(
                        value
                            .filter((eachFilter) => eachFilter.value !== 'all')
                            .map((eachMap) => eachMap.value)
                    )
                    handleFieldChange(
                        'buildingTypes',
                        value
                            .filter((eachFilter) => eachFilter.value !== 'all')
                            .map((eachMap) => eachMap.value)
                    )
                }
            } else {
                // default behaviour
                setLocalValue(value.map((eachMap) => eachMap.value))
                handleFieldChange(
                    'buildingTypes',
                    value.map((eachMap) => eachMap.value)
                )
            }
        }
    }

    return (
        <div className={classes.container}>
            <Autocomplete<NameValuePair<string>, true, true>
                multiple
                clearOnBlur
                id="buildingType"
                data-testid="dvBuildingType"
                limitTags={3}
                classes={{
                    root: classes.autocompleteRoot,
                    popupIndicator: classes.autocompletePopupIndicator,
                }}
                options={options}
                value={getAutocompleteValues(localValue)}
                disableClearable
                disableCloseOnSelect
                getOptionSelected={(option, value) =>
                    option.value === value.value
                }
                getOptionLabel={optionLabel}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        variant="outlined"
                        name="buildingType"
                        label="Building Type"
                        placeholder={
                            localValue.length === 0
                                ? 'Select Building Type'
                                : ''
                        }
                        error={!valid}
                        helperText={
                            !valid
                                ? 'Must select at least one building type.'
                                : ''
                        }
                        InputProps={{
                            ...params.InputProps,
                            startAdornment:
                                localValue.length === 0 ? (
                                    params.InputProps.startAdornment
                                ) : (
                                    <div
                                        className={
                                            classes.autocompleteInputAdornment
                                        }
                                    >
                                        {params.InputProps.startAdornment}
                                    </div>
                                ),
                            endAdornment:
                                localValue.length === 0 ? (
                                    params.InputProps.endAdornment
                                ) : (
                                    <div
                                        className={
                                            classes.autocompleteInputAdornment
                                        }
                                    >
                                        {params.InputProps.endAdornment}
                                    </div>
                                ),
                        }}
                        autoComplete="off"
                    />
                )}
                renderTags={(value, getTagProps) => {
                    if (value.some((each) => each.value === 'all')) {
                        return (
                            <Chip
                                {...getTagProps({
                                    index: ProductBuildingType.length,
                                })}
                                className="dvBuildingTypeSelected"
                                classes={{ label: classes.chipLabel }}
                                variant="outlined"
                                size="small"
                                label={<SingleLineEllipsis text="All" />}
                            />
                        )
                    }
                    return value.map(
                        (option, index) =>
                            option.value !== 'all' && (
                                <Chip
                                    {...getTagProps({ index })}
                                    className="dvBuildingTypeSelected"
                                    classes={{
                                        label: clsx(
                                            classes.chipLabel,
                                            index === 2 &&
                                                classes.chipLabelWidth
                                        ),
                                    }}
                                    variant="outlined"
                                    size="small"
                                    label={
                                        <SingleLineEllipsis
                                            text={option.name}
                                        />
                                    }
                                />
                            )
                    )
                }}
                renderOption={(option, state) => {
                    const selectAllProps =
                        ProductBuildingType.length > 0 && option.value === 'all'
                            ? {
                                  checked:
                                      ProductBuildingType.length ===
                                      localValue.length,
                              }
                            : {}
                    return (
                        <>
                            <Checkbox
                                color="primary"
                                icon={
                                    <CheckBoxOutlineBlankIcon fontSize="small" />
                                }
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                className={classes.checkbox}
                                checked={state.selected}
                                {...selectAllProps}
                            />
                            <Typography component="span" variant="h5">
                                {optionLabel(option)}
                            </Typography>
                        </>
                    )
                }}
                onChange={handleChange}
            />
        </div>
    )
}
